import React, { useEffect, useRef } from 'react';

/**
 * Camera feed display with Hot Zone indicator
 * Border changes color based on confidence:
 * - White: idle (no detection)
 * - Yellow: detecting (low-medium confidence)
 * - Blue: high confidence visual match
 * - Green: barcode match (100%)
 */
const CameraFeed = ({
  videoRef,
  status = 'idle',
  confidence = 0,
  lastMatch = null,
  className = ''
}) => {
  const containerRef = useRef(null);
  const overlayRef = useRef(null);

  // Update overlay position when container resizes
  useEffect(() => {
    const updateOverlay = () => {
      if (!containerRef.current || !overlayRef.current) return;

      const { width, height } = containerRef.current.getBoundingClientRect();

      // Hot Zone is 60% of the smaller dimension, centered
      const hotZoneSize = Math.min(width, height) * 0.6;
      const x = (width - hotZoneSize) / 2;
      const y = (height - hotZoneSize) / 2;

      overlayRef.current.style.left = `${x}px`;
      overlayRef.current.style.top = `${y}px`;
      overlayRef.current.style.width = `${hotZoneSize}px`;
      overlayRef.current.style.height = `${hotZoneSize}px`;
    };

    updateOverlay();
    window.addEventListener('resize', updateOverlay);

    // Also observe container size changes
    const observer = new ResizeObserver(updateOverlay);
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateOverlay);
      observer.disconnect();
    };
  }, []);

  // Determine border color based on status and confidence
  const getBorderStyle = () => {
    switch (status) {
      case 'matched':
        return {
          borderColor: '#22c55e', // green-500
          shadowColor: 'rgba(34, 197, 94, 0.5)',
          glowIntensity: 20
        };
      case 'high-confidence':
        return {
          borderColor: '#3b82f6', // blue-500
          shadowColor: 'rgba(59, 130, 246, 0.4)',
          glowIntensity: 15
        };
      case 'detecting':
        return {
          borderColor: '#eab308', // yellow-500
          shadowColor: 'rgba(234, 179, 8, 0.3)',
          glowIntensity: 10
        };
      case 'scanning':
        return {
          borderColor: '#94a3b8', // slate-400
          shadowColor: 'rgba(148, 163, 184, 0.2)',
          glowIntensity: 5
        };
      default:
        return {
          borderColor: 'rgba(255, 255, 255, 0.5)',
          shadowColor: 'transparent',
          glowIntensity: 0
        };
    }
  };

  const borderStyle = getBorderStyle();

  // Get status text
  const getStatusText = () => {
    switch (status) {
      case 'matched':
        return 'Match found!';
      case 'high-confidence':
        return `${Math.round(confidence * 100)}% match`;
      case 'detecting':
        return 'Analyzing...';
      case 'scanning':
        return 'Scanning...';
      default:
        return 'Put item here';
    }
  };

  return (
    <div
      ref={containerRef}
      className={`relative bg-black overflow-hidden ${className}`}
      style={{ aspectRatio: '4/3' }}
    >
      {/* Video element */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        onLoadedMetadata={(e) => {
          // Ensure video plays when metadata is loaded
          e.target.play().catch(err => console.warn('Video play failed:', err));
        }}
        className="w-full h-full object-cover"
      />

      {/* Dark overlay outside hot zone */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-black/30" />
      </div>

      {/* Hot Zone overlay */}
      <div
        ref={overlayRef}
        className="absolute pointer-events-none transition-all duration-300"
        style={{
          border: `3px solid ${borderStyle.borderColor}`,
          borderRadius: '12px',
          boxShadow: borderStyle.glowIntensity > 0
            ? `0 0 ${borderStyle.glowIntensity}px ${borderStyle.shadowColor}, inset 0 0 ${borderStyle.glowIntensity / 2}px ${borderStyle.shadowColor}`
            : 'none',
          background: 'transparent'
        }}
      >
        {/* Clear the dark overlay inside the hot zone */}
        <div
          className="absolute inset-0 -m-[3px] rounded-xl"
          style={{
            background: 'transparent',
            boxShadow: 'inset 0 0 0 1000px rgba(0,0,0,0.3)'
          }}
        />

        {/* Corner brackets */}
        <div className="absolute -top-1 -left-1 w-6 h-6 border-t-3 border-l-3 rounded-tl-lg"
             style={{ borderColor: borderStyle.borderColor }} />
        <div className="absolute -top-1 -right-1 w-6 h-6 border-t-3 border-r-3 rounded-tr-lg"
             style={{ borderColor: borderStyle.borderColor }} />
        <div className="absolute -bottom-1 -left-1 w-6 h-6 border-b-3 border-l-3 rounded-bl-lg"
             style={{ borderColor: borderStyle.borderColor }} />
        <div className="absolute -bottom-1 -right-1 w-6 h-6 border-b-3 border-r-3 rounded-br-lg"
             style={{ borderColor: borderStyle.borderColor }} />

        {/* Confidence bar at bottom of zone */}
        {confidence > 0 && status !== 'matched' && (
          <div className="absolute bottom-3 left-3 right-3 h-1.5 bg-black/40 rounded-full overflow-hidden">
            <div
              className="h-full transition-all duration-200 rounded-full"
              style={{
                width: `${confidence * 100}%`,
                backgroundColor: borderStyle.borderColor
              }}
            />
          </div>
        )}

        {/* Scanning animation */}
        {(status === 'scanning' || status === 'detecting') && (
          <div className="absolute inset-0 overflow-hidden rounded-lg">
            <div
              className="absolute left-0 right-0 h-0.5 rounded-full"
              style={{
                backgroundColor: borderStyle.borderColor,
                animation: 'scan 2s ease-in-out infinite',
                top: '50%'
              }}
            />
          </div>
        )}

        {/* Success checkmark animation */}
        {status === 'matched' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center animate-ping" />
            <div className="absolute w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
        )}
      </div>

      {/* Status text at bottom */}
      <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
        <div className="flex items-center justify-center gap-2 text-white text-sm font-medium">
          {status === 'matched' && (
            <div className="w-2 h-2 bg-green-400 rounded-full" />
          )}
          {status === 'high-confidence' && (
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
          )}
          {(status === 'scanning' || status === 'detecting') && (
            <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />
          )}
          <span>{getStatusText()}</span>
        </div>
      </div>

      {/* Match result overlay */}
      {lastMatch && (
        <div className="absolute top-3 left-3 right-3 bg-green-500/95 text-white px-3 py-2 rounded-lg flex items-center gap-2 shadow-lg animate-slide-down">
          {/* Product image thumbnail */}
          {(lastMatch.item?.images?.[0]?.large_url || lastMatch.item?.image_url) && (
            <img
              src={lastMatch.item?.images?.[0]?.large_url || lastMatch.item?.image_url}
              alt=""
              className="w-8 h-8 rounded object-cover"
            />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate">
              {lastMatch.item?.title || lastMatch.item?.asin || 'Item matched'}
            </p>
            <p className="text-xs opacity-80">
              {lastMatch.matchType === 'barcode' ? 'Barcode' : `${Math.round((lastMatch.confidence || 0) * 100)}% visual`}
            </p>
          </div>
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      )}

      {/* Animations */}
      <style>{`
        @keyframes scan {
          0%, 100% { transform: translateY(-80px); opacity: 0; }
          25% { opacity: 1; }
          50% { transform: translateY(80px); opacity: 1; }
          75% { opacity: 1; }
        }
        @keyframes slide-down {
          from { transform: translateY(-20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-slide-down {
          animation: slide-down 0.3s ease-out;
        }
        .border-t-3 { border-top-width: 3px; }
        .border-b-3 { border-bottom-width: 3px; }
        .border-l-3 { border-left-width: 3px; }
        .border-r-3 { border-right-width: 3px; }
      `}</style>
    </div>
  );
};

export default CameraFeed;
